import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import API_CONFIG from '../config/api';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import { apiFetch } from '../api/client';
import { withUnidade } from '../utils/storageByUnit';
import { withUnidadeESetor, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';

// DashboardPage - OTIMIZADO

const BACKEND_URL = API_CONFIG.BACKEND_URL;

// Componente de Card de Métrica - MEMOIZADO
const MetricCard = React.memo(({ title, value, icon, color, onClick }) => (
  <Card 
    sx={{ 
      height: '100%',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.3)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
      } : {}
    }}
    onClick={onClick}
  >
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={700} color={color} sx={{ mb: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        <Box 
          sx={{ 
            p: 2, 
            borderRadius: '50%', 
            background: `${color}15`,
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
));


const COLORS = [
  '#007AFF', '#FF9800', '#4CAF50', '#E91E63', '#9C27B0', '#00BCD4', '#FFC107', 
  '#8BC34A', '#FF5722', '#3F51B5', '#CDDC39', '#F44336', '#009688', '#795548', '#607D8B'
];

export default function DashboardPage() {
  console.log('DashboardPage está sendo renderizado');
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const visaoFiscal = isFiscalSetorial(user, rbacRole || getUserRole(user));
  const setorFiscal = getFiscalSetor(user, fiscalSetor);
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle] = useState('');
  const [modalContratos] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  const [selectedSector, setSelectedSector] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [contratoGestao, setContratoGestao] = useState(null);
  const [hoveredSector] = useState(null);
  const [nfsPendentesAteste, setNfsPendentesAteste] = useState(0);
  const [drawerContrato, setDrawerContrato] = useState(null); // { ...contrato, tipo: 'vencendo'|'vencido' }
  const [drawerJustificativa, setDrawerJustificativa] = useState('');
  const [drawerAcao, setDrawerAcao] = useState('JUSTIFICATIVA');
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerMsg, setDrawerMsg] = useState({ type: '', text: '' });
  
  const scopeOpts = { user, rbacRole: rbacRole || getUserRole(user), fiscalSetor };

  const fetchDashboardData = useCallback(async () => {
    try {
      // TODO Backend: /api/dashboard/overview deve respeitar ?setor= para FISCAL
      const url = withUnidadeESetor('/api/dashboard/overview', unidadeSelecionada, scopeOpts);
      const response = await apiFetch(url);
      if (!response.ok) throw new Error('Erro ao buscar dados do dashboard');
      const data = await response.json();
      setDashboardData(data);
      if (visaoFiscal && setorFiscal) {
        setSelectedSector(setorFiscal);
        const sectorData = data.contratosPorSetor?.find((s) => s.setor_nome === setorFiscal);
        if (sectorData) {
          setFilteredData({
            ...data,
            estatisticas: {
              total_contratos: sectorData.total_contratos || 0,
              contratos_ativos: sectorData.contratos_ativos || 0,
              contratos_vencidos: (data.contratosVencidos || []).filter((c) => c.setor_nome === setorFiscal).length,
              valor_mensal_total: sectorData.valor_mensal || 0
            },
            contratosPorSetor: [sectorData],
            contratosVencendo: (data.contratosVencendo || []).filter((c) => c.setor_nome === setorFiscal),
            contratosVencidos: (data.contratosVencidos || []).filter((c) => c.setor_nome === setorFiscal)
          });
        } else {
          setFilteredData(data);
        }
      } else {
        setFilteredData(data);
        setSelectedSector(null);
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada, visaoFiscal, setorFiscal, user, rbacRole, fiscalSetor]);

  const fetchContratoGestao = useCallback(async () => {
    if (visaoFiscal) {
      setContratoGestao(null);
      return;
    }
    try {
      const response = await apiFetch(withUnidade('/api/contratos-gestao/ativo', unidadeSelecionada));
      if (!response.ok) throw new Error('Erro ao buscar contrato de gestão');
      const data = await response.json();
      setContratoGestao(data);
    } catch (error) {
      console.error('Erro ao carregar contrato de gestão:', error);
      setContratoGestao(null);
    }
  }, [unidadeSelecionada, visaoFiscal]);

  const fetchNfsPendentesAteste = useCallback(async () => {
    if (!visaoFiscal || !setorFiscal) {
      setNfsPendentesAteste(0);
      return;
    }
    try {
      const url = withUnidadeESetor('/api/notas-fiscais', unidadeSelecionada, scopeOpts);
      const res = await apiFetch(url);
      if (!res.ok) return;
      const rows = await res.json();
      const pendentes = (Array.isArray(rows) ? rows : []).filter(
        (n) => String(n.status || '').toLowerCase().includes('avaliacao') ||
          String(n.status || '').toLowerCase() === 'pendente_avaliacao'
      );
      setNfsPendentesAteste(pendentes.length);
    } catch (e) {
      setNfsPendentesAteste(0);
    }
  }, [visaoFiscal, setorFiscal, unidadeSelecionada, user, rbacRole, fiscalSetor]);

  // Função para filtrar dados por setor ou Top 10
  const filterBySector = (sectorName) => {
    if (!dashboardData) return;
    
    if (sectorName === 'top10') {
      // Mostrar Top 10 contratos mais caros
      const top10Contracts = getTop10Contracts();
      const totalValue = top10Contracts.reduce((sum, contract) => sum + (contract.valorNumerico || 0), 0);
      
      const filtered = {
        ...dashboardData,
        estatisticas: {
          total_contratos: top10Contracts.length,
          contratos_ativos: top10Contracts.length,
          contratos_vencidos: 0,
          valor_mensal_total: totalValue
        },
        contratosPorSetor: [{
          setor_nome: 'Top 10 Contratos',
          total_contratos: top10Contracts.length,
          contratos_ativos: top10Contracts.length,
          valor_mensal: totalValue,
          contratos: top10Contracts.map((contrato, index) => ({
            ...contrato,
            percentualTop10: ((contrato.valorNumerico / totalValue) * 100).toFixed(1)
          }))
        }],
        contratosVencendo: dashboardData.contratosVencendo.filter(c => 
          top10Contracts.some(tc => tc.numeroContrato === c.numeroContrato)
        ),
        contratosVencidos: dashboardData.contratosVencidos.filter(c => 
          top10Contracts.some(tc => tc.numeroContrato === c.numeroContrato)
        )
      };
      setFilteredData(filtered);
      setSelectedSector('top10');
    } else if (sectorName === null) {
      // Mostrar todos os dados
      setFilteredData(dashboardData);
      setSelectedSector(null);
    } else {
      // Filtrar por setor específico
      const sectorData = dashboardData.contratosPorSetor.find(s => s.setor_nome === sectorName);
      if (sectorData) {
        const filtered = {
          ...dashboardData,
          estatisticas: {
            total_contratos: sectorData.total_contratos,
            contratos_ativos: sectorData.contratos_ativos,
            contratos_vencidos: 0, // Calcular baseado nos contratos filtrados
            valor_mensal_total: sectorData.valor_mensal
          },
          contratosPorSetor: [sectorData],
          contratosVencendo: dashboardData.contratosVencendo.filter(c => c.setor_nome === sectorName),
          contratosVencidos: dashboardData.contratosVencidos.filter(c => c.setor_nome === sectorName)
        };
        setFilteredData(filtered);
        setSelectedSector(sectorName);
      }
    }
  };

  // Função para obter contratos detalhados do setor selecionado
  const getSectorContracts = () => {
    if (!selectedSector || !dashboardData) return [];
    
    const sectorData = dashboardData.contratosPorSetor.find(s => s.setor_nome === selectedSector);
    return sectorData ? sectorData.contratos : [];
  };

  // Função para obter os 5 contratos mais caros de um setor específico
  const getTop5SectorContracts = () => {
    if (!selectedSector || !dashboardData) return [];
    
    const contracts = getSectorContracts();
    // Filtrar contratos ativos e ordenar por valor
    const activeContracts = contracts.filter(c => !c.finalizado);
    const sortedContracts = activeContracts.sort((a, b) => {
      const valueA = a.valorNumerico || 0;
      const valueB = b.valorNumerico || 0;
      return valueB - valueA;
    });
    
    // Retornar apenas os 5 mais caros
    return sortedContracts.slice(0, 5);
  };

  // Função para obter os 7 setores mais caros (para o gráfico)
  const getTop7Sectors = () => {
    if (!dashboardData || !dashboardData.contratosPorSetor) return [];
    
    // Ordenar setores por valor mensal (do maior para o menor)
    const sortedSectors = [...dashboardData.contratosPorSetor].sort((a, b) => {
      const valueA = a.valor_mensal || 0;
      const valueB = b.valor_mensal || 0;
      return valueB - valueA;
    });
    
    // Retornar apenas os 7 mais caros
    return sortedSectors.slice(0, 7);
  };

  // Função para obter todos os setores (para a lista lateral)
  const getAllSectors = () => {
    if (!dashboardData || !dashboardData.contratosPorSetor) return [];
    
    // Ordenar setores por valor mensal (do maior para o menor)
    const sortedSectors = [...dashboardData.contratosPorSetor].sort((a, b) => {
      const valueA = a.valor_mensal || 0;
      const valueB = b.valor_mensal || 0;
      return valueB - valueA;
    });
    
    // Retornar todos os setores ordenados
    return sortedSectors;
  };

  // Função para obter os 10 contratos mais caros (todos os setores)
  const getTop10Contracts = () => {
    if (!dashboardData) return [];
    
    // Coletar todos os contratos de todos os setores
    const allContracts = [];
    dashboardData.contratosPorSetor.forEach(setor => {
      if (setor.contratos) {
        setor.contratos.forEach(contrato => {
          allContracts.push({
            ...contrato,
            setor_nome: setor.setor_nome
          });
        });
      }
    });
    
    // Ordenar por valor (maior para menor) e pegar os 10 primeiros
    const top10 = allContracts
      .sort((a, b) => (b.valorNumerico || 0) - (a.valorNumerico || 0))
      .slice(0, 10);
    
    // Calcular o valor total dos top 10
    const totalValue = top10.reduce((sum, contract) => sum + (contract.valorNumerico || 0), 0);
    
    // Adicionar porcentagem para cada contrato baseado no valor total dos top 10
    return top10.map(contrato => ({
      ...contrato,
      percentualTop10: ((contrato.valorNumerico / totalValue) * 100).toFixed(1)
    }));
  };



  const abrirDrawerContrato = (contrato, tipo) => {
    if (!contrato?.id) {
      setDrawerMsg({ type: 'error', text: 'Contrato sem ID — recarregue o dashboard.' });
      return;
    }
    setDrawerContrato({ ...contrato, tipo });
    setDrawerJustificativa(contrato.justificativa_nao_renovacao || '');
    setDrawerAcao(contrato.acao_renovacao || 'JUSTIFICATIVA');
    setDrawerMsg({ type: '', text: '' });
  };

  const fecharDrawerContrato = () => {
    setDrawerContrato(null);
    setDrawerJustificativa('');
    setDrawerAcao('JUSTIFICATIVA');
    setDrawerMsg({ type: '', text: '' });
  };

  const salvarAcompanhamento = async () => {
    if (!drawerContrato?.id) return;
    setDrawerSaving(true);
    setDrawerMsg({ type: '', text: '' });
    try {
      const res = await apiFetch(`/api/prestadores/${drawerContrato.id}/acompanhamento-renovacao`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          justificativa_nao_renovacao: drawerJustificativa,
          acao_renovacao: drawerAcao
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setDrawerMsg({ type: 'success', text: 'Acompanhamento salvo.' });
      setDrawerContrato((prev) =>
        prev
          ? {
              ...prev,
              justificativa_nao_renovacao: drawerJustificativa,
              acao_renovacao: drawerAcao
            }
          : prev
      );
      await fetchDashboardData();
    } catch (e) {
      setDrawerMsg({ type: 'error', text: e.message || 'Erro ao salvar' });
    } finally {
      setDrawerSaving(false);
    }
  };

  const toggleFinalizadoDrawer = async (desativar) => {
    if (!drawerContrato?.id) return;
    setDrawerSaving(true);
    setDrawerMsg({ type: '', text: '' });
    try {
      const res = await apiFetch(`/api/prestadores/${drawerContrato.id}/finalizar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ finalizado: desativar ? 1 : 0 })
      });
      if (!res.ok) throw new Error('Erro ao atualizar status do contrato');
      setDrawerMsg({
        type: 'success',
        text: desativar ? 'Contrato desativado.' : 'Contrato ativado.'
      });
      setDrawerContrato((prev) => (prev ? { ...prev, finalizado: desativar } : prev));
      await fetchDashboardData();
      if (desativar) {
        // Sai da lista de vencendo/vencidos (filtro finalizado)
        setTimeout(() => fecharDrawerContrato(), 800);
      }
    } catch (e) {
      setDrawerMsg({ type: 'error', text: e.message || 'Erro ao alterar status' });
    } finally {
      setDrawerSaving(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchContratoGestao();
    fetchNfsPendentesAteste();
  }, [fetchDashboardData, fetchContratoGestao, fetchNfsPendentesAteste]);

  // Listener para o evento de refresh do header
  useEffect(() => {
    const handleRefresh = () => {
      fetchDashboardData();
      fetchContratoGestao();
      fetchNfsPendentesAteste();
    };
    
    window.addEventListener('refreshDashboard', handleRefresh);
    
    return () => {
      window.removeEventListener('refreshDashboard', handleRefresh);
    };
  }, [fetchDashboardData, fetchContratoGestao, fetchNfsPendentesAteste]);

  // Efeito para esconder automaticamente as boas-vindas após 10 segundos
  useEffect(() => {
    if (showWelcome && !loading) {
      const timer = setTimeout(() => {
        setShowWelcome(false);
      }, 10000); // 10 segundos
      
      return () => clearTimeout(timer);
    }
  }, [showWelcome, loading]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };



  if (loading) {
  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            Carregando dados do Dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e3f2fd 50%, #f8fafc 100%)',
      p: { xs: 1, sm: 2 },
      m: 0,
      overflow: 'auto'
    }}>
      <VisaoRestritaBanner />
      
      {/* Seção de Propósito */}
        <Box 
          sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            mb: 2,
            position: 'relative'
          }}
        >
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <Box sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            boxShadow: '0 4px 12px rgba(25,118,210,0.3)'
          }}>
            <Typography sx={{ color: 'white', fontSize: '20px' }}>❤️</Typography>
          </Box>
          <Typography 
            variant="h5" 
            fontWeight={700} 
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Cuidar e Salvar Vidas
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Nosso compromisso fundamental com a vida e o bem-estar
          </Typography>
          </Box>


      {/* Cards de Estatísticas */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={visaoFiscal ? 'Contratos no Seu Setor' : 'Total de Contratos'}
            value={filteredData?.estatisticas?.total_contratos || 0}
            icon={<DescriptionIcon />}
            color="#007AFF"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={visaoFiscal ? 'Contratos Ativos no Seu Setor' : 'Contratos Ativos'}
            value={filteredData?.estatisticas?.contratos_ativos || 0}
            icon={<TrendingUpIcon />}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={visaoFiscal ? 'Notas Pendentes de Ateste' : 'Contratos Vencidos'}
            value={visaoFiscal ? nfsPendentesAteste : (filteredData?.estatisticas?.contratos_vencidos || 0)}
            icon={visaoFiscal ? <WarningIcon /> : <ErrorIcon />}
            color={visaoFiscal ? '#FF9800' : '#F44336'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {visaoFiscal ? (
            <MetricCard
              title="Contratos Vencidos (setor)"
              value={filteredData?.estatisticas?.contratos_vencidos || 0}
              icon={<ErrorIcon />}
              color="#F44336"
            />
          ) : (
          <Card 
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  {contratoGestao && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block', 
                        color: '#1976d2', 
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}>
                        N° TTG: {contratoGestao.numero_contrato}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        display: 'block', 
                        color: '#4CAF50', 
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}>
                        Valor: {formatCurrency(contratoGestao.valor_total_recebido)}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="h4" fontWeight={700} color="#FF9800" sx={{ mb: 1 }}>
                    {formatCurrency(filteredData?.estatisticas?.valor_mensal_total || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Valor Mensal Total</span>
                    {contratoGestao && filteredData?.estatisticas?.valor_mensal_total && (
                      <span style={{ color: '#FF9800', fontWeight: 600, fontSize: '0.8rem' }}>
                        {((filteredData.estatisticas.valor_mensal_total / contratoGestao.valor_total_recebido) * 100).toFixed(1)}% do TTG
                      </span>
                    )}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: '50%', 
                    background: '#FF980015',
                    color: '#FF9800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <MonetizationOnIcon />
                </Box>
              </Box>
            </CardContent>
          </Card>
          )}
        </Grid>
      </Grid>

      {/* Seção de Gráficos - Topo */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2 }}>
        {/* Gráfico de Pizza - Contratos por Setor */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            height: '525px',
           
          }}>
            <CardContent>
              {/* Filtro no canto superior esquerdo */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                  <Typography variant="h6" fontWeight={600} color="primary">
                   {visaoFiscal
                     ? `Contratos — ${setorFiscal || 'Seu Setor'}`
                     : selectedSector === 'top10' ? 'Top 10 Contratos Mais Caros' : 
                    selectedSector ? `Top 5 Contratos - ${selectedSector}` : 
                    'Top 7 Setores Mais Caros'}
                    </Typography>
                {!visaoFiscal && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="setor-filter-label">Filtrar por Setor</InputLabel>
                  <Select
                    labelId="setor-filter-label"
                    value={selectedSector === null ? 'todos' : selectedSector}
                    label="Filtrar por Setor"
                    onChange={(e) => {
                      const value = e.target.value;
                      filterBySector(value === 'todos' ? null : value);
                    }}
                            sx={{ 
                      fontSize: '0.875rem',
                      '& .MuiSelect-select': {
                        py: 1
                      }
                    }}
                  >
                    <MenuItem value="todos">Todos os Setores</MenuItem>
                    <MenuItem value="top10">Top 10 Contratos Mais Caros</MenuItem>
                    {dashboardData?.contratosPorSetor?.map((setor) => (
                      <MenuItem key={setor.setor_nome} value={setor.setor_nome}>
                        {setor.setor_nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )}
                  </Box>

                    <Box sx={{ 
                height: { xs: 250, sm: 300, md: 400 }, 
                display: 'flex', 
                alignItems: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 0 }
              }}>
                {filteredData?.contratosPorSetor && filteredData.contratosPorSetor.length > 0 ? (
                  <>
                    {/* Gráfico de pizza expandido */}
                    <Box sx={{ 
                      width: { xs: '100%', md: '65%' }, 
                      height: { xs: 250, md: '100%' },
                      mb: { xs: 2, md: 0 }
                    }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={selectedSector === 'top10' ? 
                              // Mostrar 10 contratos mais caros
                              getTop10Contracts().map((contrato, index) => ({
                                name: `${contrato.numeroContrato} - ${contrato.empresa.substring(0, 15)}...`,
                                value: contrato.valorNumerico,
                                percentual: contrato.percentualTop10,
                                color: COLORS[index % COLORS.length],
                                valor_mensal: contrato.valorNumerico,
                                setor_nome: contrato.setor_nome
                              })) :
                              selectedSector && selectedSector !== 'top10' ? 
                              // Mostrar 5 contratos mais caros do setor selecionado
                              getTop5SectorContracts().map((contrato, index) => ({
                                name: `${contrato.numeroContrato} - ${contrato.empresa.substring(0, 15)}...`,
                                value: contrato.valorNumerico,
                                percentual: contrato.percentualSetor,
                                color: COLORS[index % COLORS.length],
                                valor_mensal: contrato.valorNumerico,
                                setor_nome: contrato.setor_nome
                              })) :
                              // Mostrar 7 setores mais caros (Todos os Setores)
                              getTop7Sectors().map((setor, index) => {
                                const totalValue = dashboardData?.estatisticas?.valor_mensal_total || 1;
                                const percentage = ((setor.valor_mensal / totalValue) * 100).toFixed(1);
                                return {
                                  name: setor.setor_nome,
                                  value: setor.valor_mensal,
                                  color: COLORS[index % COLORS.length],
                                  percentual: percentage,
                                  valor_mensal: setor.valor_mensal,
                                  total_contratos: setor.total_contratos
                                };
                              })
                            }
                            cx="50%"
                            cy="50%"
                            outerRadius={130}
                            innerRadius={40}
                            dataKey="value"
                            paddingAngle={2}
                            label={({ percentual, name, cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                              // Calcular posição central da fatia
                              const RADIAN = Math.PI / 180;
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                              const x = cx + radius * Math.cos(-midAngle * RADIAN);
                              const y = cy + radius * Math.sin(-midAngle * RADIAN);
                              
                              return (
                                <text 
                                  x={x} 
                                  y={y} 
                                  fill="white" 
                                  textAnchor={x > cx ? 'start' : 'end'} 
                                  dominantBaseline="central"
                                  fontSize="12"
                                  fontWeight="bold"
                                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                                >
                                  {percentual}%
                                </text>
                              );
                            }}
                            labelLine={false}
                          >
                            {(selectedSector === 'top10' ? 
                              getTop10Contracts() :
                              selectedSector && selectedSector !== 'top10' ?
                              getTop5SectorContracts() :
                              getTop7Sectors()
                            ).map((entry, index) => {
                              const isHovered = hoveredSector === entry.name || hoveredSector === entry.setor_nome || hoveredSector === 'top10';
                              const isSelected = selectedSector === entry.name || selectedSector === entry.setor_nome || selectedSector === 'top10';
                              return (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={COLORS[index % COLORS.length]}
                                  stroke={isHovered || isSelected ? '#000' : 'none'}
                                  strokeWidth={isHovered || isSelected ? 2 : 0}
                                  style={{
                                    filter: isHovered ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                                    transformOrigin: 'center'
                                  }}
                                />
                              );
                            })}
                      </Pie>
                          <RechartsTooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                // Tooltip para contratos (Top 10 ou Top 5 do setor)
                                  return (
                                    <Box sx={{ 
                                      p: 2, 
                                      bgcolor: 'rgba(255,255,255,0.95)', 
                                      borderRadius: 2, 
                                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                      border: '1px solid rgba(0,0,0,0.1)'
                                    }}>
                                      <Typography variant="subtitle2" fontWeight={600} color="primary">
                                        {data.name}
                                      </Typography>
                                    {data.setor_nome && (
                                      <Typography variant="body2">
                                        <strong>Setor:</strong> {data.setor_nome}
                                      </Typography>
                                    )}
                                      <Typography variant="body2">
                                        <strong>Valor:</strong> {formatCurrency(data.value)}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Porcentagem:</strong> {data.percentual}%
                                      </Typography>
                                    </Box>
                                  );
                              }
                              return null;
                            }}
                          />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                
                    {/* Lista de setores/contratos à direita */}
                    <Box sx={{ 
                      width: { xs: '100%', md: '35%' }, 
                      pl: { md: 2 },
                      mt: { xs: 2, md: 0 },
                      height: '100%'
                    }}>
                      <Typography variant="subtitle2" fontWeight={600} mb={1} color="primary">
                        {selectedSector === 'top10' ? 'Top 10 Contratos' : 
                         selectedSector ? `Contratos - ${selectedSector}` : 'Todos os Setores'}
                        </Typography>
                      <Box sx={{ maxHeight: 350, overflowY: 'auto' }}>
                        {selectedSector === 'top10' ? (
                          // Mostrar Top 10 contratos mais caros
                          getTop10Contracts() && getTop10Contracts().length > 0 ? getTop10Contracts().map((contrato, index) => (
                            <Box 
                              key={contrato.numeroContrato}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 1,
                                p: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(25,118,210,0.05)',
                                border: '1px solid rgba(25,118,210,0.1)',
                                '&:hover': { backgroundColor: 'rgba(25,118,210,0.1)' }
                              }}
                            >
                              <Box 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: COLORS[index % COLORS.length],
                                  mr: 1
                                }} 
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>
                                  {contrato.numeroContrato}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  {contrato.empresa}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main', fontWeight: 600 }}>
                                  {formatCurrency(contrato.valorNumerico)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 600 }}>
                                  {contrato.percentualTop10}% do Top 10
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 500 }}>
                                  {contrato.setor_nome}
                                </Typography>
                              </Box>
                            </Box>
                          )) : (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                              Nenhum contrato encontrado
                            </Typography>
                          )
                        ) : selectedSector ? (
                          // Mostrar todos os contratos do setor selecionado
                          getSectorContracts() && getSectorContracts().length > 0 ? getSectorContracts().map((contrato, index) => (
                            <Box 
                              key={contrato.numeroContrato}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 1,
                                p: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(25,118,210,0.05)',
                                border: '1px solid rgba(25,118,210,0.1)',
                                '&:hover': { backgroundColor: 'rgba(25,118,210,0.1)' }
                              }}
                            >
                              <Box 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: COLORS[index % COLORS.length],
                                  mr: 1
                                }} 
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>
                                  {contrato.numeroContrato}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  {contrato.empresa}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main', fontWeight: 600 }}>
                                  {formatCurrency(contrato.valorNumerico)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 600 }}>
                                  {contrato.percentualTop10}% do Top 10
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 500 }}>
                                  {contrato.setor_nome}
                                </Typography>
                              </Box>
                            </Box>
                          )) : (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                              Nenhum contrato encontrado
                            </Typography>
                          )
                        ) : (
                          // Mostrar todos os setores
                          getAllSectors() && getAllSectors().length > 0 ? getAllSectors().map((setor, index) => {
                            const totalValue = dashboardData?.estatisticas?.valor_mensal_total || 1;
                            const valuePercentage = ((setor.valor_mensal / totalValue) * 100).toFixed(1);
                            
                            return (
                              <Box 
                                key={setor.setor_nome}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 1,
                                  p: 0.5,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  backgroundColor: selectedSector === setor.setor_nome ? 'rgba(25,118,210,0.1)' : 'transparent',
                                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                                }}
                                onClick={() => filterBySector(setor.setor_nome)}
                              >
                                <Box 
                                  sx={{ 
                                    width: 12, 
                                    height: 12, 
                                    borderRadius: '50%', 
                                    backgroundColor: COLORS[index % COLORS.length],
                                    mr: 1
                                  }} 
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', fontWeight: 600, wordBreak: 'break-word' }}>
                                    {setor.setor_nome}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                    {setor.total_contratos} contratos
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main', fontWeight: 600 }}>
                                    {formatCurrency(setor.valor_mensal || 0)} ({valuePercentage}%)
                                  </Typography>
                              </Box>
                            </Box>
                            );
                          }) : (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                              Nenhum setor encontrado
                            </Typography>
                          )
                        )}
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    Nenhum dado disponível
                </Typography>
                )}
              </Box>
              </CardContent>
            </Card>
          </Grid>

        {/* Gráfico de Barras - Valores por Setor */}
        <Grid item xs={12} md={6}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            height: '525px'
          
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2} textAlign="center">
                {selectedSector && selectedSector !== 'top10' ? `Valores - ${selectedSector}` : 
                 selectedSector === 'top10' ? 'Valores - Top 10 Contratos' : 'Valores por Setor'}
                </Typography>
              <Box sx={{ height: { xs: 250, sm: 300, md: 350 } }}>
                {filteredData?.contratosPorSetor && filteredData.contratosPorSetor.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={selectedSector && selectedSector !== 'top10' ? 
                        // Mostrar todos os contratos do setor selecionado
                        getSectorContracts().filter(c => !c.finalizado).map(contrato => ({
                          nome: contrato.numeroContrato,
                          empresa: contrato.empresa.substring(0, 20) + '...',
                          setor: contrato.setor_nome,
                          valorMensal: contrato.valorNumerico || 0,
                          valorTotal: contrato.valorNumerico * 12 || 0,
                          percentual: contrato.percentualSetor
                        })) :
                        selectedSector === 'top10' ?
                        // Mostrar Top 10 contratos mais caros
                        getTop10Contracts().map(contrato => ({
                          nome: contrato.numeroContrato,
                          empresa: contrato.empresa.substring(0, 20) + '...',
                          setor: contrato.setor_nome,
                          valorMensal: contrato.valorNumerico || 0,
                          valorTotal: contrato.valorNumerico * 12 || 0,
                          percentual: contrato.percentualTop10
                        })) :
                        // Mostrar setores (comportamento original)
                        filteredData.contratosPorSetor.map(setor => ({
                          nome: setor.setor_nome,
                          valorMensal: setor.valor_mensal || 0,
                          valorTotal: setor.valor_mensal * 12 || 0
                        }))
                      }
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                      <XAxis 
                        dataKey={selectedSector ? "nome" : "nome"} 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={12}
                      />
                      <YAxis 
                        fontSize={12}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box sx={{ 
                                p: 2, 
                                bgcolor: 'rgba(255,255,255,0.95)', 
                                borderRadius: 2, 
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                border: '1px solid rgba(0,0,0,0.1)',
                                minWidth: 200
                              }}>
                                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                  {selectedSector || data.empresa ? `${data.nome} - ${data.empresa}` : data.nome}
                                </Typography>
                                
                                {data.setor && !selectedSector && (
                                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                    <strong>Setor:</strong> {data.setor}
                                  </Typography>
                                )}
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      bgcolor: '#4CAF50', 
                                      borderRadius: '2px',
                                      mr: 1
                                    }} />
                                    <Typography variant="body2">
                                      <strong>Valor Mensal:</strong>
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color="#4CAF50">
                                      {formatCurrency(data.valorMensal || 0)}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      bgcolor: '#9C27B0', 
                                      borderRadius: '2px',
                                      mr: 1
                                    }} />
                                    <Typography variant="body2">
                                      <strong>Valor Total (12x):</strong>
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color="#9C27B0">
                                      {formatCurrency(data.valorTotal || 0)}
                                    </Typography>
                                  </Box>
                                  
                                  {data.percentual && (
                                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'primary.main', fontWeight: 600 }}>
                                      {selectedSector ? `${data.percentual}% do setor` : `${data.percentual}% do Top 10`}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="valorMensal" 
                        fill="#4CAF50" 
                        name="Valor Mensal"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="valorTotal" 
                        fill="#9C27B0" 
                        name="Valor Total"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    Nenhum dado disponível
                  </Typography>
                )}
              </Box>
              </CardContent>
            </Card>
          </Grid>
      </Grid>

      {/* Seção de Listas - Meio */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2 }}>
        {/* Contratos Vencendo (até 60 dias) */}
        <Grid item xs={12} md={6}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" color="primary" fontWeight={600}>
                  Contratos Vencendo
                </Typography>
                <Chip
                  label={`${filteredData?.contratosVencendo?.length || 0} · 60 dias`}
                  color="warning"
                  size="small"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Role para ver todos. Clique para acompanhar renovação.
              </Typography>
              <Box sx={{ maxHeight: { xs: 280, sm: 340, md: 400 }, overflowY: 'auto', pr: 0.5 }}>
                {(filteredData?.contratosVencendo || []).map((contrato) => (
                  <Box
                    key={contrato.id || contrato.numeroContrato}
                    onClick={() => abrirDrawerContrato(contrato, 'vencendo')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      p: 1.25,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid transparent',
                      '&:hover': { bgcolor: 'rgba(255,152,0,0.08)', borderColor: 'rgba(255,152,0,0.25)' }
                    }}
                  >
                    <WarningIcon color="warning" sx={{ mr: 1, fontSize: 18 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600}>
                        {contrato.numeroContrato}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {contrato.prestador_nome} — {contrato.dias_para_vencimento} dias
                        {contrato.setor_nome ? ` · ${contrato.setor_nome}` : ''}
                      </Typography>
                    </Box>
                    {contrato.justificativa_nao_renovacao ? (
                      <Chip size="small" label="Com just." variant="outlined" sx={{ ml: 0.5 }} />
                    ) : null}
                  </Box>
                ))}
                {(!filteredData?.contratosVencendo || filteredData.contratosVencendo.length === 0) && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    Nenhum contrato vencendo em 60 dias
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
      </Grid>

        {/* Contratos Vencidos */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" color="error" fontWeight={600}>
                  Contratos Vencidos
            </Typography>
                <Chip
                  label={filteredData?.contratosVencidos?.length || 0}
                  color="error"
                  size="small"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Role para ver todos. Clique para justificar ou desativar.
              </Typography>
              <Box sx={{ maxHeight: { xs: 280, sm: 340, md: 400 }, overflowY: 'auto', pr: 0.5 }}>
                {(filteredData?.contratosVencidos || []).map((contrato) => (
                  <Box
                    key={contrato.id || contrato.numeroContrato}
                    onClick={() => abrirDrawerContrato(contrato, 'vencido')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      p: 1.25,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid transparent',
                      '&:hover': { bgcolor: 'rgba(244,67,54,0.06)', borderColor: 'rgba(244,67,54,0.25)' }
                    }}
                  >
                    <ErrorIcon color="error" sx={{ mr: 1, fontSize: 18 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600}>
                        {contrato.numeroContrato}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {contrato.prestador_nome} — {contrato.dias_vencido} dias vencido
                        {contrato.setor_nome ? ` · ${contrato.setor_nome}` : ''}
                      </Typography>
                    </Box>
                    {contrato.justificativa_nao_renovacao ? (
                      <Chip size="small" label="Com just." variant="outlined" sx={{ ml: 0.5 }} />
                    ) : null}
                  </Box>
                ))}
                {(!filteredData?.contratosVencidos || filteredData.contratosVencidos.length === 0) && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    Nenhum contrato vencido
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        </Grid>

      {/* Seções Adicionais - Notas Fiscais e Alertas */}
      <Grid container spacing={2} sx={{ px: 2, mt: 2, mb: 2 }}>
        {/* Notas Fiscais Pendentes */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Notas Fiscais Pendentes
            </Typography>
                <Chip
                  label="0"
                  color="info"
                  size="small"
                />
                  </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                Nenhuma nota fiscal pendente
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Alertas Recentes */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" color="error" fontWeight={600}>
                  Alertas Recentes
            </Typography>
                <Chip
                  label="0"
                  color="error"
                  size="small"
                />
            </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                Nenhum alerta recente
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>




      {/* Modal de detalhes */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>{modalTitle}</Typography>
            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {modalContratos.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              Nenhum contrato encontrado.
            </Typography>
          ) : (
            <Box>
              {modalContratos.map((contrato, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight="bold">
                        {contrato.numeroContrato}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2">
                        {contrato.prestador_nome}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        {contrato.setor_nome}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Drawer
        anchor="right"
        open={Boolean(drawerContrato)}
        onClose={fecharDrawerContrato}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" fontWeight={700}>
              {drawerContrato?.tipo === 'vencido' ? 'Contrato vencido' : 'Contrato vencendo'}
            </Typography>
            <IconButton onClick={fecharDrawerContrato} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </Box>

          {drawerContrato && (
            <>
              <Chip
                size="small"
                color={drawerContrato.tipo === 'vencido' ? 'error' : 'warning'}
                label={
                  drawerContrato.tipo === 'vencido'
                    ? `${drawerContrato.dias_vencido} dias vencido`
                    : `${drawerContrato.dias_para_vencimento} dias para vencer`
                }
                sx={{ alignSelf: 'flex-start', mb: 2 }}
              />
              <Typography variant="subtitle1" fontWeight={700}>
                {drawerContrato.numeroContrato}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {drawerContrato.prestador_nome}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Setor: {drawerContrato.setor_nome || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Vigência fim: {drawerContrato.data_vencimento || '—'}
                {drawerContrato.valorMes != null ? ` · Valor: ${drawerContrato.valorMes}` : ''}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Ação de acompanhamento</InputLabel>
                <Select
                  label="Ação de acompanhamento"
                  value={drawerAcao}
                  onChange={(e) => setDrawerAcao(e.target.value)}
                >
                  <MenuItem value="JUSTIFICATIVA">Registrar justificativa</MenuItem>
                  <MenuItem value="ADITIVO">Em processo de aditivo / renovação</MenuItem>
                  <MenuItem value="NOVO_CONTRATO">Novo contrato em elaboração</MenuItem>
                  <MenuItem value="ENCERRAR">Encerrar / não renovar</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Justificativa / observações"
                value={drawerJustificativa}
                onChange={(e) => setDrawerJustificativa(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                placeholder="Motivo de ainda não ter sido renovado, andamento do aditivo, etc."
                sx={{ mb: 2 }}
              />

              {drawerMsg.text && (
                <Alert severity={drawerMsg.type || 'info'} sx={{ mb: 2 }}>
                  {drawerMsg.text}
                </Alert>
              )}

              <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={drawerSaving}
                  onClick={salvarAcompanhamento}
                >
                  Salvar acompanhamento
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => {
                    navigate('/prestadores', {
                      state: {
                        highlightContrato: drawerContrato.numeroContrato,
                        prestadorId: drawerContrato.id
                      }
                    });
                  }}
                >
                  Abrir em Prestadores (aditivo)
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(drawerContrato.finalizado)}
                      disabled={drawerSaving}
                      onChange={(e) => toggleFinalizadoDrawer(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    drawerContrato.finalizado
                      ? 'Contrato desativado (inativo)'
                      : 'Desativar contrato (mesma flag de Prestadores)'
                  }
                />
                {drawerContrato.finalizado && (
                  <Button size="small" onClick={() => toggleFinalizadoDrawer(false)} disabled={drawerSaving}>
                    Reativar contrato
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
} 
